// D53/D54 — one-touch intent buffer + stale-hold guard. Tapping PASS/THROUGH/SHOOT while NOT possessing
// arms that verb for a short window; the armed bit rides every outgoing input, so the sim's fresh-claim
// rule (a held kick bit within INTENT_TICKS of the claim fires first-time) executes the kick the instant
// possession arrives — press early, volley on arrival.
//
// D54: the mirror guard — a kick bit merely HELD OVER when possession arrives (the very press that
// launched your PREVIOUS pass, still down when control switches to the receiver at reception) is STALE:
// it is masked from the outgoing inputs until physically released, or the fresh-claim rule would read it
// as the receiver's one-touch intent and instantly pass again (the "double pass" bug). A fresh press on
// the reception tick itself, and any deliberately armed intent, still fire.
//
// Pure and tick-driven (unit-testable, no DOM).

import { BTN } from '@fafi/shared';

const KICK_BITS = BTN.PASS | BTN.THROUGH | BTN.SHOOT;

export class KickIntent {
  private bits = 0;
  private untilTick = -1;
  private prevButtons = 0;
  private hadBall = false;
  private stale = 0; // held-over kick bits, masked until physical release (D54)

  constructor(
    private readonly windowTicks = 18, // ~0.6 s for the ball to arrive
    private readonly graceTicks = 4, // ticks the armed bit persists after gaining the ball (the sim fires within 1–2)
  ) {}

  reset(): void {
    this.bits = 0;
    this.untilTick = -1;
    this.prevButtons = 0;
    this.hadBall = false;
    this.stale = 0;
  }

  /** T-TCH-013 (FAFI Touch §2.4): the armed one-touch verbs, for the button glow. Reports the buffer's
   *  own kick bits — the LOFT modifier rides `bits` invisibly — so the glow can never disagree with
   *  what the sim will fire. Freshness is as of the last apply(); the glow is read right after it. */
  get armed(): number {
    return this.bits & KICK_BITS;
  }

  /**
   * Feed every outgoing input; returns `buttons` with armed intent ORed in and stale holds masked out.
   *
   * D135 — `airborne` (a free ball above the volley band) hands the SHOOT arm to the SIM. This buffer used
   * to fake an 18-tick hold for it, which is why a TAP worked only for short flights and did so
   * invisibly: past the window the declaration silently evaporated with nothing on screen saying so.
   * `aerial.TAP_ARM` keeps a declared finish alive server-side until contact, the ground or a claim, so
   * re-emitting the bit here would be a second, weaker copy of that rule — and the two would disagree
   * about exactly the flights the player notices. The GROUND one-touch buffer (PASS/THROUGH, and SHOOT
   * onto a rolling ball) is untouched: there is no sim-side stickiness for those.
   */
  // D155: NOT optional. It defaulted to false, and both shipped call sites simply omitted it — so this
  // whole rule was dead code from the day it landed. A required parameter makes the typechecker the test.
  apply(buttons: number, hasBall: boolean, tick: number, airborne: boolean): number {
    const pressed = buttons & ~this.prevButtons & KICK_BITS;
    this.stale &= buttons; // a physical release un-stales that bit
    if (airborne) this.bits &= ~BTN.SHOOT; // D135: the sim holds this one
    if (!hasBall && pressed !== 0) {
      // D110: the buffered press captures the LOFT modifier AS IT WAS AT PRESS TIME and re-emits it with
      // the kick bit. Without this a buffered high pass fires FLAT whenever V is released during the
      // ball's flight — the player's declared intent was "chip", and the buffer must not silently
      // downgrade it. (Multiple simultaneous kick presses resolve by the sim's SHOOT>THROUGH>PASS priority.)
      this.bits = (airborne ? pressed & ~BTN.SHOOT : pressed) | (buttons & BTN.LOFT);
      this.untilTick = tick + this.windowTicks;
    }
    if (!this.hadBall && hasBall) {
      // gaining the ball: bits held over from BEFORE (not this tick's press, not an armed intent) are
      // leftovers of a kick already spent — never this ball's one-touch order (D54)
      this.stale |= buttons & KICK_BITS & ~this.bits & ~pressed;
    }
    if (this.bits !== 0 && hasBall) this.untilTick = Math.min(this.untilTick, tick + this.graceTicks);
    if (tick > this.untilTick) this.bits = 0;
    this.hadBall = hasBall;
    this.prevButtons = buttons;
    return (buttons & ~this.stale) | this.bits;
  }
}
